import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Select } from '../ui/Select'

interface DeviceDetails {
  status: 'online' | 'offline' | 'warning' | 'error'
  operating_system: string
  ip_address: string
  uptime: string
  last_seen_display: string
  notifications_count: number
  cpu_usage: number
  memory_usage: number
  storage_usage: number
  logged_in_user: string
  has_full_data: boolean
  // Additional UI fields
  device_id_display: string
  user_email: string
  user_full_name: string
  ticket_count: number
  last_active: string
  device_type_display: string
  organization_name: string
}

interface EditDeviceModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (details: DeviceDetails) => void
  deviceId: string
  currentDetails?: Partial<DeviceDetails>
}

export const EditDeviceModal = ({ isOpen, onClose, onSave, currentDetails }: EditDeviceModalProps) => {
        const [details, setDetails] = useState<DeviceDetails>({
          status: 'offline',
          operating_system: '',
          ip_address: '',
          uptime: '',
          last_seen_display: '',
          notifications_count: 0,
          cpu_usage: 0,
          memory_usage: 0,
          storage_usage: 0,
          logged_in_user: '',
          has_full_data: false,
          // Additional UI fields
          device_id_display: '',
          user_email: '',
          user_full_name: '',
          ticket_count: 0,
          last_active: '',
          device_type_display: '',
          organization_name: 'WCD'
        })

  const [isLoading, setIsLoading] = useState(false)

  // Initialize form with current details
  useEffect(() => {
    if (currentDetails) {
      setDetails(prev => ({ 
        ...prev, 
        ...currentDetails,
        // Ensure has_full_data is true when editing existing device
        has_full_data: true
      }))
    }
  }, [currentDetails])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && currentDetails) {
      console.log('EditDeviceModal: Populating form with currentDetails:', currentDetails)
      setDetails(prev => ({ 
        ...prev, 
        ...currentDetails,
        // Ensure has_full_data is true when editing existing device
        has_full_data: true
      }))
    }
  }, [isOpen, currentDetails])

  const handleInputChange = (field: keyof DeviceDetails, value: string | number | boolean) => {
    setDetails(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
            // Convert empty strings to null for timestamp fields and clean up data
            const cleanedDetails = {
              ...details,
              // Convert empty string to empty string for timestamp field
              last_active: details.last_active || '',
              // Keep other fields as is, but convert empty strings to null for optional fields
              last_seen_display: details.last_seen_display || '',
              uptime: details.uptime || '',
              ip_address: details.ip_address || '',
              operating_system: details.operating_system || '',
              device_id_display: details.device_id_display || '',
              user_email: details.user_email || '',
              user_full_name: details.user_full_name || '',
              device_type_display: details.device_type_display || '',
              organization_name: details.organization_name || '',
              logged_in_user: details.logged_in_user || '',
              // Always set has_full_data to true when saving device details
              has_full_data: true
            }
      
      await onSave(cleanedDetails)
      onClose()
    } catch (error) {
      console.error('Error saving device details:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Device Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <Select
                        value={details.status}
                        onChange={(e) => handleInputChange('status', e.target.value as 'online' | 'offline' | 'warning' | 'error')}
                        className="w-full"
                      >
                        <option value="online">Online</option>
                        <option value="offline">Offline</option>
                        <option value="warning">Warning</option>
                        <option value="error">Error</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Operating System
                      </label>
                <Select
                  value={details.operating_system}
                  onChange={(e) => handleInputChange('operating_system', e.target.value)}
                  className="w-full"
                >
                  <option value="">Select Operating System</option>
                  <option value="Windows 11 Pro">Windows 11 Pro</option>
                  <option value="Windows 11 Home">Windows 11 Home</option>
                  <option value="Windows 10 Pro">Windows 10 Pro</option>
                  <option value="Windows 10 Home">Windows 10 Home</option>
                  <option value="macOS Sonoma">macOS Sonoma</option>
                  <option value="macOS Ventura">macOS Ventura</option>
                  <option value="macOS Monterey">macOS Monterey</option>
                  <option value="Ubuntu 22.04 LTS">Ubuntu 22.04 LTS</option>
                  <option value="Ubuntu 20.04 LTS">Ubuntu 20.04 LTS</option>
                  <option value="CentOS 8">CentOS 8</option>
                  <option value="Red Hat Enterprise Linux">Red Hat Enterprise Linux</option>
                  <option value="Debian 11">Debian 11</option>
                  <option value="Other">Other</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IP Address
                </label>
                <input
                  type="text"
                  value={details.ip_address}
                  onChange={(e) => handleInputChange('ip_address', e.target.value)}
                  placeholder="e.g., 192.168.2.87"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uptime
                </label>
                <input
                  type="text"
                  value={details.uptime}
                  onChange={(e) => handleInputChange('uptime', e.target.value)}
                  placeholder="e.g., 2 days, 14 hours"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Seen
                </label>
                <input
                  type="text"
                  value={details.last_seen_display}
                  onChange={(e) => handleInputChange('last_seen_display', e.target.value)}
                  placeholder="e.g., 5 minutes ago"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Resource Usage */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Resource Usage (%)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CPU Usage
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={details.cpu_usage}
                  onChange={(e) => handleInputChange('cpu_usage', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Memory Usage
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={details.memory_usage}
                  onChange={(e) => handleInputChange('memory_usage', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Usage
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={details.storage_usage}
                  onChange={(e) => handleInputChange('storage_usage', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Additional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notifications Count
                </label>
                <input
                  type="number"
                  min="0"
                  value={details.notifications_count}
                  onChange={(e) => handleInputChange('notifications_count', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logged In User
                </label>
                <Select
                  value={details.logged_in_user}
                  onChange={(e) => handleInputChange('logged_in_user', e.target.value)}
                  className="w-full"
                >
                  <option value="">Select User</option>
                  <option value="janedoe@acme.com">janedoe@acme.com</option>
                  <option value="mike.johnson@msp3.com">mike.johnson@msp3.com</option>
                  <option value="david.lee@msp3.com">david.lee@msp3.com</option>
                  <option value="tom.wilson@msp3.com">tom.wilson@msp3.com</option>
                  <option value="james.taylor@msp3.com">james.taylor@msp3.com</option>
                  <option value="sarah.smith@wcd.com">sarah.smith@wcd.com</option>
                  <option value="john.doe@wcd.com">john.doe@wcd.com</option>
                  <option value="alex.brown@wcd.com">alex.brown@wcd.com</option>
                  <option value="emma.davis@wcd.com">emma.davis@wcd.com</option>
                  <option value="michael.wilson@wcd.com">michael.wilson@wcd.com</option>
                  <option value="">No User Logged In</option>
                </Select>
              </div>
            </div>
          </div>

          {/* Device Display Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Device Display Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Device ID Display
                </label>
                <input
                  type="text"
                  value={details.device_id_display}
                  onChange={(e) => handleInputChange('device_id_display', e.target.value)}
                  placeholder="e.g., USR-002, WS-HR-01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Device Type Display
                </label>
                <Select
                  value={details.device_type_display}
                  onChange={(e) => handleInputChange('device_type_display', e.target.value)}
                  className="w-full"
                >
                  <option value="">Select Device Type</option>
                  <option value="Laptop">Laptop</option>
                  <option value="Desktop">Desktop</option>
                  <option value="Server">Server</option>
                  <option value="Mobile">Mobile</option>
                  <option value="Tablet">Tablet</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Full Name
                </label>
                <input
                  type="text"
                  value={details.user_full_name}
                  onChange={(e) => handleInputChange('user_full_name', e.target.value)}
                  placeholder="e.g., Mike Johnson"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Email
                </label>
                <input
                  type="email"
                  value={details.user_email}
                  onChange={(e) => handleInputChange('user_email', e.target.value)}
                  placeholder="e.g., mike.johnson@msp3.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket Count
                </label>
                <input
                  type="number"
                  min="0"
                  value={details.ticket_count}
                  onChange={(e) => handleInputChange('ticket_count', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Active
                </label>
                <input
                  type="datetime-local"
                  value={details.last_active}
                  onChange={(e) => handleInputChange('last_active', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Full Data Toggle */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={details.has_full_data}
                onChange={(e) => handleInputChange('has_full_data', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Device has full monitoring data
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-4 text-sm font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
